import React, { ReactElement, useEffect, useState, useMemo } from 'react';
import moment from 'moment-timezone';
import { Row, Col, Button, Spin } from 'antd';
import { useQuery, useQueryClient } from 'react-query';

import ViewPanel from 'vcomponents/Form/ViewPanel';
import { getWelfare, getPolicyHistoryWel, getWelfarePolicyList } from 'apis/captainpayment/member';

import GroupList from './GroupList';
import PolicyList from './PolicyList';
import PolicyHistoryMeModal from './modal/PolicyHistoryMeModal';

moment.tz.setDefault('Asia/Seoul');

interface CompanyWelfarePolicyProps {}

function CompanyWelfarePolicy(props: CompanyWelfarePolicyProps): ReactElement {
  const queryClient = useQueryClient();
  const [welfareName, setWelfareName] = useState(undefined);
  const [welfareIdx, setWelfareIdx] = useState(undefined);
  const [visible, setVisible] = useState(false);
  const [params, setParams] = useState({
    page: 1,
    pageRow: 15
  });

  const { data, isSuccess } = useQuery('getWelfare', () => getWelfare(), {
    keepPreviousData: true
  });

  const { data: policyData = {}, isLoading } = useQuery(
    ['getWelfarePolicyList', welfareIdx],
    () => welfareIdx && getWelfarePolicyList(welfareIdx)
  );

  const { data: historyList, isSuccess: historySuccess } = useQuery('getPolicyHistoryWel', () =>
    getPolicyHistoryWel(params)
  );

  // Welfare
  useEffect(() => {
    queryClient.prefetchQuery('getWelfare', () => getWelfare());
  }, [queryClient]);

  const getWelfarePolicy = async (idx, name) => {
    try {
      await queryClient.prefetchQuery(['getWelfarePolicyList', idx], () => getWelfarePolicyList(idx));
      setWelfareIdx(idx);
      setWelfareName(name);
    } catch (e) {
      console.log(e);
    }
  };

  const group = useMemo(() => {
    if (isSuccess) {
      const { group: groupData } = data;
      getWelfarePolicy(groupData[0].idx, groupData[0].name);
      return groupData;
    }
    return false;
  }, [data]);

  // Policy
  const policy = useMemo(() => {
    if (policyData && policyData.policy) {
      return policyData.policy;
    }
    return false;
  }, [policyData, policyData.policy]);

  // History
  const historyPolicyModal = async (page) => {
    const pageInfo = { ...params, ...page };
    await queryClient.prefetchQuery('getPolicyHistoryWel', () => getPolicyHistoryWel(pageInfo));
    setVisible(true);
  };

  const welfareHistory = useMemo(() => {
    const obj = {
      history: [],
      paging: {}
    };
    return historySuccess ? historyList : obj;
  }, [historyList, visible]);

  const closeModal = () => {
    setVisible(false);
  };

  const modalFn = useMemo(() => {
    return {
      visible,
      closeModal,
      welfareHistory,
      historyPolicyModal
    };
  }, [visible, welfareHistory]);

  const PolicyFn = useMemo(() => {
    return {
      policy,
      welfareName,
      getWelfarePolicy,
      welfareIdx
    };
  }, [policy, welfareIdx, welfareName]);

  return (
    <Spin spinning={isLoading}>
      <ViewPanel
        title="welfare.policy.header"
        buttons={[
          <Button key="historyPolicy" onClick={() => historyPolicyModal(params)}>
            변경 내역
          </Button>
        ]}
      >
        <Row>
          <Col span={4}>
            <GroupList group={group} onClick={getWelfarePolicy} />
          </Col>
          <Col span={20}>
            <PolicyList PolicyFn={PolicyFn} />
          </Col>
        </Row>
        <PolicyHistoryMeModal modalFn={modalFn} />
      </ViewPanel>
    </Spin>
  );
}

export default CompanyWelfarePolicy;
